import React, { createContext, ReactNode, useContext, useEffect, useMemo } from "react";
import { AiEventStreamStore } from "../store/AiEventStreamStore";
import { API_HOST } from "../config/_entrypoint";

type AiEventStreamProviderProps = {
  children: ReactNode;
  eventStreamPath?: string;
  eventName?: string;
  withCredentials?: boolean;
  maxStoredEvents?: number;
  maxStoredDeltas?: number;
  streamUrlFactory?: (streamId: string) => string;
};

const defaultEventStreamPath = "api/ai/event_stream";

type AiEventStreamRegistry = {
  getStore: (streamKey?: number | null) => AiEventStreamStore;
  resetAll: () => void;
};

const AiEventStreamContext = createContext<AiEventStreamRegistry | null>(null);

const buildEventStreamUrl = (apiHost: string, eventStreamPath: string, streamId: string): string => {
  const normalizedApiHost = apiHost.endsWith("/") ? apiHost : `${apiHost}/`;
  const normalizedPath = eventStreamPath.replace(/^\/+|\/+$/g, "");

  return `${normalizedApiHost}${normalizedPath}/${encodeURIComponent(streamId)}`;
};

export function AiEventStreamProvider({
  children,
  eventStreamPath = defaultEventStreamPath,
  eventName = "aiEvent",
  withCredentials = false,
  maxStoredEvents,
  maxStoredDeltas,
  streamUrlFactory,
}: AiEventStreamProviderProps) {
  const apiHost = API_HOST;

  const resolvedStreamUrlFactory = useMemo(() => {
    if (streamUrlFactory) {
      return streamUrlFactory;
    }

    if (!apiHost) {
      throw new Error("AiEventStreamProvider requires either apiHost or streamUrlFactory.");
    }

    return (streamId: string) => buildEventStreamUrl(apiHost, eventStreamPath, streamId);
  }, [apiHost, eventStreamPath, streamUrlFactory]);

  const registry = useMemo<AiEventStreamRegistry>(() => {
    const stores = new Map<number | null, AiEventStreamStore>();

    return {
      getStore: (streamKey = null) => {
        let store = stores.get(streamKey);
        if (!store) {
          store = new AiEventStreamStore({
            streamUrlFactory: resolvedStreamUrlFactory,
            eventName,
            withCredentials,
            maxStoredEvents,
            maxStoredDeltas,
          });
          stores.set(streamKey, store);
        }
        return store;
      },
      resetAll: () => stores.forEach((store) => store.reset()),
    };
  }, [eventName, maxStoredDeltas, maxStoredEvents, resolvedStreamUrlFactory, withCredentials]);

  useEffect(() => {
    return () => {
      registry.resetAll();
    };
  }, [registry]);

  return <AiEventStreamContext.Provider value={registry}>{children}</AiEventStreamContext.Provider>;
}

export function useAiEventStreamRegistry(): AiEventStreamRegistry {
  const registry = useContext(AiEventStreamContext);

  if (!registry) {
    throw new Error("useAiEventStreamRegistry must be used inside AiEventStreamProvider.");
  }

  return registry;
}

export function useAiEventStreamStore(streamKey: number | null = null): AiEventStreamStore {
  return useAiEventStreamRegistry().getStore(streamKey);
}
